import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, GraduationCap } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a tutor invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A tutor invoice should include your name and contact details, the student or parent name, a unique invoice number and date, payment due date, a description of tutoring sessions (subject, number of sessions, dates), your hourly or session rate, any materials or resource fees, applicable tax if you are registered, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often should tutors send invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Weekly or monthly billing is most common for tutors. Weekly invoices work well for intensive tutoring or exam preparation periods. Monthly billing suits ongoing regular sessions and creates a predictable payment schedule. For block bookings (e.g., 10 sessions prepaid), invoice the full block at the start before the sessions begin.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I charge for missed tutoring sessions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if your cancellation policy is agreed upfront. Add a "Late cancellation fee" line item and reference your policy. Most tutors require 24–48 hours notice for cancellation without charge. Without a clear policy in writing, it is difficult to enforce cancellation fees — so include this in your initial agreement with the client.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do private tutors need to charge VAT or GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In the UK, private tutoring is exempt from VAT for individuals providing education in a subject they are qualified to teach. In Australia, education services are generally GST-free. In India, tutorial services are taxable at 18% GST if turnover exceeds ₹20 lakh. In the US, tutoring services are generally not subject to sales tax, though rules vary by state. Check your country\'s specific rules.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this tutor invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free tutor invoice template. Fill in your session details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function TutorInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Tutor Invoice Template: Free PDF for Private Tutors in UK, Ireland & India | Invilic"
        description="Free tutor invoice template for private tutors worldwide. UK VAT-exempt, Ireland VAT-exempt, India GST rules covered. Bill for lessons and materials. No sign-up needed."
        canonical="https://www.invilic.com/tutor-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Tutor Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for private tutors and educators. Bill for sessions, block bookings, and materials. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Tutor
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Academic & school tutors', desc: 'Bill for maths, science, English, and exam preparation sessions by the hour or block.' },
              { title: 'Online tutors', desc: 'Invoice students or parents for virtual sessions, recorded lessons, and online resources.' },
              { title: 'Music & arts tutors', desc: 'Bill for piano, guitar, singing, and art lessons with flexible session or monthly billing.' },
              { title: 'Language tutors', desc: 'Issue professional invoices for language lessons, conversation practice, and immersion programmes.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Tutor Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Tutor name and contact details',
              'Student / parent name and address',
              'Invoice number and date',
              'Payment due date',
              'Subject and session description',
              'Number of sessions and dates',
              'Hourly rate or session fee',
              'Materials and resource fee',
              'Late cancellation fee (if applicable)',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-green-100 dark:border-green-800/30 bg-green-50 dark:bg-green-900/10">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Tutoring Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your sessions', desc: 'Enter student details, sessions, your rate, and any materials fees.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Tutor Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Country-specific VAT rules */}
        <section className="mb-16 p-6 rounded-2xl border border-green-100 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Tutor Invoicing Rules by Country</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { country: 'United Kingdom', rule: 'Private tutoring by an individual teacher in their specialist subject is VAT-exempt. Register for HMRC Self Assessment if income exceeds £1,000/year.' },
              { country: 'Ireland', rule: 'Grinds and private tuition are generally VAT-exempt under the education exemption. Check Revenue.ie for your specific situation. File a self-assessed income tax return annually.' },
              { country: 'India', rule: 'Tutorial services attract 18% GST (SAC code 999290) if annual turnover exceeds ₹20 lakh. Below the threshold, issue standard invoices without GST.' },
            ].map(({ country, rule }) => (
              <div key={country} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-green-100 dark:border-green-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{country}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{rule}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Tutoring Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Tutor Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
